import { useEffect, useMemo, useState } from "react";
import "@/App.css";
import axios from "axios";
import {
  BookOpen,
  ChevronRight,
  Edit3,
  GraduationCap,
  Instagram,
  Mail,
  Menu,
  MessageCircle,
  Phone,
  Plus,
  Save,
  Scissors,
  Sparkles,
  Trash2,
  X,
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const defaultSettings = {
  business_name: "Parvez Signature",
  email: "contact@parvezsignature.com",
  whatsapp: "+234 708 923 2855",
  phone: "+234 708 923 2855",
  instagram: "https://www.instagram.com/parvezsignatures",
  tiktok: "#",
  youtube: "#",
  address: "Lagos, Nigeria",
  opening_hours: "Monday to Saturday, by appointment",
  currency_primary: "NGN",
  seven_haven_signature_enabled: true,
  seven_haven_url: "https://sevenhaventechnologies.com",
  seven_haven_mark_variant: "gold",
};

const fallbackData = {
  settings: defaultSettings,
  collections: [],
  products: [],
  courses: [],
  testimonials: [],
  media: [],
};

const navItems = ["Home", "Shop", "Collections", "Fashion Academy", "Gallery", "About", "Contact"];

const emptyForms = {
  products: { name: "", category: "Custom Orders", description: "", price: "Enquire", images: [], videos: [], featured: true, available: true },
  collections: { name: "", description: "", image: "", priority: 9, featured: true },
  courses: { title: "", summary: "", covers: "", audience: "", duration: "", outcomes: [], enrolment: "Chat with us on WhatsApp to reserve a place.", image: "", featured: true },
  testimonials: { client_name: "", text: "", category: "Client", photo: "", featured: true },
  media: { type: "image", title: "", alt: "", src: "", category: "general", featured: true },
};

function imageUrl(src) {
  if (!src) return "/media/hero-garment.jpg";
  return src.startsWith("data:") || src.startsWith("http") ? src : src;
}

function waLink(settings, text = "Hello Parvez Signature, I would like to make an enquiry.") {
  const phone = (settings?.whatsapp || "").replace(/[^0-9]/g, "");
  return `https://wa.me/${phone}?text=${encodeURIComponent(text)}`;
}

function echo(event_type, label = "", page = "", metadata = {}) {
  const session_id = window.sessionStorage.getItem("echo_session") || crypto.randomUUID();
  window.sessionStorage.setItem("echo_session", session_id);
  axios.post(`${API}/echo/events`, { event_type, label, page, metadata, session_id }).catch(() => {});
}

function trackLink(eventType, label, page) {
  echo(eventType, label, page, { path: window.location.pathname });
}

function SectionTitle({ eyebrow, title, text, align = "left" }) {
  return (
    <div className={`section-title ${align === "center" ? "center" : ""}`}>
      <span>{eyebrow}</span>
      <h2>{title}</h2>
      {text && <p>{text}</p>}
    </div>
  );
}

function CTAButtons({ settings, secondary = true }) {
  return (
    <div className="button-row">
      <a className="btn primary" href={waLink(settings, "Hello Parvez Signature, I would like to book a custom order.")} onClick={() => trackLink("whatsapp_click", "Book Custom Order", "CTA")} data-testid="book-order-button">
        Book Custom Order <ChevronRight size={16} />
      </a>
      {secondary && (
        <a className="btn whatsapp" href={waLink(settings)} onClick={() => trackLink("whatsapp_click", "Chat on WhatsApp", "CTA")} target="_blank" rel="noreferrer" data-testid="whatsapp-chat-button">
          <MessageCircle size={16} /> Chat on WhatsApp
        </a>
      )}
    </div>
  );
}

function Header({ settings, active, setActive }) {
  const [open, setOpen] = useState(false);
  const go = (item) => {
    setActive(item);
    echo("page_view", item, item);
    setOpen(false);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  return (
    <>
      <header className="site-header">
        <button className="brand" onClick={() => go("Home")} data-testid="header-home-link">
          <span className="monogram">PS</span>
          <span className="brand-copy"><strong>Parvez Signature</strong><small>Fashion House & Academy</small></span>
        </button>
        <nav className="desktop-nav">
          {navItems.map((item) => (
            <button key={item} className={active === item ? "active" : ""} onClick={() => go(item)}>{item}</button>
          ))}
        </nav>
        <div className="header-actions">
          <a className="mini-link" href={settings.instagram} onClick={() => trackLink("social_click", "Instagram Header", "Navigation")} target="_blank" rel="noreferrer" aria-label="Instagram"><Instagram size={18} /></a>
          <a className="header-wa" href={waLink(settings)} onClick={() => trackLink("whatsapp_click", "Header/Mobile WhatsApp", active || "Navigation")} target="_blank" rel="noreferrer">Chat on WhatsApp</a>
          <button className="menu-btn" onClick={() => setOpen(true)} aria-label="Open menu"><Menu /></button>
        </div>
      </header>
      {open && (
        <div className="mobile-menu">
          <button className="close" onClick={() => setOpen(false)}><X /></button>
          {navItems.map((item) => <button key={item} onClick={() => go(item)}>{item}</button>)}
          <a href={waLink(settings)} onClick={() => trackLink("whatsapp_click", "Header/Mobile WhatsApp", active || "Navigation")} target="_blank" rel="noreferrer">Chat on WhatsApp</a>
        </div>
      )}
    </>
  );
}

function ConversionBar({ settings }) {
  return (
    <section className="conversion-bar">
      <p>Ready to discuss a garment, fabric order or sewing class?</p>
      <div>
        <a href={waLink(settings)} onClick={() => trackLink("whatsapp_click", "Conversion Bar WhatsApp", "Conversion")} target="_blank" rel="noreferrer">Chat on WhatsApp</a>
        <a href={`mailto:${settings.email}`}>{settings.email}</a>
      </div>
    </section>
  );
}

function Home({ data, settings, setActive }) {
  const products = data.products.filter((item) => item.featured).slice(0, 6);
  const collections = [...data.collections].sort((a, b) => (a.priority || 0) - (b.priority || 0)).slice(0, 7);
  const courses = data.courses.filter((item) => item.featured).slice(0, 3);
  const testimonials = data.testimonials.filter((item) => item.featured).slice(0, 3);
  const media = data.media.filter((item) => item.featured);

  return (
    <main>
      <section className="hero" id="home">
        <div className="hero-copy">
          <span className="eyebrow">Fashion. Education. Creativity. Growth.</span>
          <h1>Fashion Designed with Purpose. Skills Shared with Passion.</h1>
          <p>Custom tailoring, premium fabrics and fashion education from Adenike, founder of Parvez Signature in Lagos, Nigeria. A warm place for bespoke dresses, bridal wear, Aso Oke, Aso Ebi, Ankara dresses, ready-to-wear and sewing classes.</p>
          <CTAButtons settings={settings} />
          <div className="hero-points">
            <span><Scissors size={16} /> Custom Orders</span>
            <span><Sparkles size={16} /> Premium Fabrics</span>
            <span><GraduationCap size={16} /> Fashion Academy</span>
          </div>
        </div>
        <div className="hero-media">
          <img src="/media/hero-garment.jpg" alt="Parvez Signature custom garment" />
          <div className="hero-card"><strong>Parvez Signature Studio</strong><span>Craftsmanship, family and sewing education.</span></div>
        </div>
      </section>

      <section className="section-pad">
        <SectionTitle eyebrow="Featured Collections" title="A fashion house with many doors" text="Custom fashion, fabrics, accessories and practical fashion education." />
        <div className="collection-grid">
          {collections.map((item) => (
            <button className="collection-card" key={item.id} onClick={() => setActive(item.name === "Fashion School" ? "Fashion Academy" : "Collections")}> 
              <img src={imageUrl(item.image)} alt={item.name} />
              <div><small>{String(item.priority || "").padStart(2, "0")}</small><h3>{item.name}</h3><p>{item.description}</p></div>
            </button>
          ))}
        </div>
      </section>

      <section className="section-pad sand">
        <SectionTitle eyebrow="Recent Creations" title="Real work from the studio" text="Dresses, fabrics and tailoring work from the studio." />
        <div className="product-grid">
          {products.map((product) => <ProductCard key={product.id} product={product} settings={settings} />)}
        </div>
      </section>

      <section className="section-pad fabric-section">
        <SectionTitle eyebrow="Fabric Marketplace" title="Browse Premium Fabrics" text="Ankara, lace, knit, sequins, Aso Oke and ready-to-sew materials." />
        <div className="fabric-tiles">
          {[
            ["Ankara", "/media/fabrics-ankara.jpg"], ["Lace & Sequins", "/media/fabrics-sequins.jpg"], ["Knit", "/media/fabrics-knit.jpg"], ["Aso Oke", "/media/fabrics-aso-oke.jpg"],
          ].map(([name, img]) => <div key={name} className="fabric-tile"><img src={img} alt={name} /><span>{name}</span></div>)}
        </div>
      </section>

      <section className="section-pad academy-preview ivory">
        <SectionTitle eyebrow="Fashion Academy" title="Learn from an experienced designer" text="Practical sewing and fashion skills for beginners and growing designers." />
        <div className="course-grid">
          {courses.map((course) => <CourseCard key={course.id} course={course} settings={settings} />)}
        </div>
      </section>

      <section className="section-pad process">
        <SectionTitle eyebrow="From Sketch to Stitch" title="How your garment comes to life" text="A clear process helps clients feel confident from first message to final fitting." align="center" />
        <div className="process-grid">
          {[
            ["01", "Consultation", "We discuss your vision, event, style and timeline."],
            ["02", "Design", "Measurements, fabric selection and concept direction."],
            ["03", "Craftsmanship", "Skilled tailoring, fitting and careful finishing."],
            ["04", "Delivery", "Your finished garment, ready to wear with confidence."],
          ].map(([n, t, d]) => <div className="process-card" key={n}><span>{n}</span><h3>{t}</h3><p>{d}</p></div>)}
        </div>
      </section>

      <section className="section-pad studio sand">
        <div>
          <SectionTitle eyebrow="Inside Our Studio" title="A clean, authentic space for learning and making" text="Workshop photos, fabric shelves, finished garments and sewing process content build trust because they show the real work behind the brand." />
          <CTAButtons settings={settings} />
        </div>
        <div className="studio-gallery">
          <img src="/media/featured-gold.jpg" alt="Parvez Signature studio garment" />
          <img src="/media/course-streetwear.jpg" alt="Streetwear fashion class" />
          <img src="/media/fabrics-knit.jpg" alt="Fabric stacks" />
        </div>
      </section>

      <section className="section-pad">
        <SectionTitle eyebrow="Fashion Categories" title="Shop by craft and occasion" text="Dresses, evening wear, couture, ready-to-wear, Ankara, lace, luxury fabrics and tailoring work." />
        <div className="visual-strip">
          {["/media/featured-bridal.jpg", "/media/client-style-1.jpg", "/media/featured-evening.jpg", "/media/fabrics-sequins.jpg", "/media/fabrics-aso-oke.jpg"].map((src) => <img key={src} src={src} alt="Parvez Signature fashion category" loading="lazy" />)}
        </div>
      </section>
      <Testimonials testimonials={testimonials} />
      <section className="section-pad about-teaser ivory">
        <SectionTitle eyebrow="Meet Adenike" title="Founder of Parvez Signature" text="Adenike built Parvez Signature through years of dedication to fashion, tailoring and teaching. The brand combines bespoke craftsmanship, premium fabrics and practical fashion education." />
        <blockquote>“I believe fashion should inspire confidence, creativity, and continuous growth.”</blockquote>
        <button className="btn outline" onClick={() => setActive("About")}>Meet the Founder</button>
      </section>
      <InstagramSection settings={settings} media={media} />
      <FAQSection settings={settings} />
      <ConversionBar settings={settings} />
    </main>
  );
}

function ProductCard({ product, settings }) {
  const [open, setOpen] = useState(false);
  const img = product.images?.[0] || "/media/hero-garment.jpg";
  return (
    <article className="product-card">
      <img src={imageUrl(img)} alt={product.name} loading="lazy" onError={() => echo("media_error", product.name, "Shop")} />
      <div>
        <span>{product.category}</span>
        <h3>{product.name}</h3>
        <div className="card-footer"><strong>{product.price}</strong><button onClick={() => { echo("product_view", product.name, "Product"); setOpen(true); }}>View Details</button><a href={waLink(settings, `Hello Parvez Signature, I am interested in ${product.name}.`)} onClick={() => trackLink("whatsapp_click", product.name, "Product")} target="_blank" rel="noreferrer">Chat on WhatsApp</a></div>
      </div>
      {open && <ProductModal product={product} settings={settings} onClose={() => setOpen(false)} />}
    </article>
  );
}

function ProductModal({ product, settings, onClose }) {
  return (
    <div className="modal-backdrop" role="dialog" aria-modal="true">
      <div className="modal-card product-modal">
        <button className="close-modal" onClick={onClose}><X /></button>
        <div className="product-modal-grid">
          <img src={imageUrl(product.images?.[0])} alt={product.name} />
          <div>
            <span className="modal-kicker">{product.category}</span>
            <h2>{product.name}</h2>
            <p className="lead">{product.description || "A Parvez Signature piece made with attention to fit, fabric and finishing."}</p>
            <strong>{product.price}</strong>
            <a className="btn whatsapp" href={waLink(settings, `Hello Parvez Signature, I would like to order ${product.name}.`)} onClick={() => trackLink("whatsapp_click", `${product.name} details`, "Product Modal")} target="_blank" rel="noreferrer">Chat on WhatsApp</a>
          </div>
        </div>
      </div>
    </div>
  );
}

function CourseCard({ course, settings }) {
  const [open, setOpen] = useState(false);
  return (
    <article className="course-card">
      <img src={imageUrl(course.image || "/media/course-streetwear.jpg")} alt={course.title} loading="lazy" onError={() => echo("media_error", course.title, "Fashion Academy")} />
      <div>
        <span><BookOpen size={14} /> Course</span>
        <h3>{course.title}</h3>
        <p><strong>{course.duration}</strong></p>
        <div className="course-level">Beginner / Intermediate</div>
        <div className="button-row compact"><button className="btn outline" onClick={() => setOpen(true)} data-testid="course-read-more-button">Read More</button><a className="btn whatsapp" href={waLink(settings, `Hello Parvez Signature, I want to join ${course.title}.`)} onClick={() => trackLink("whatsapp_click", course.title, "Course")} target="_blank" rel="noreferrer">Chat on WhatsApp</a></div>
      </div>
      {open && <CourseModal course={course} settings={settings} onClose={() => setOpen(false)} />}
    </article>
  );
}

function CourseModal({ course, settings, onClose }) {
  return (
    <div className="modal-backdrop" role="dialog" aria-modal="true">
      <div className="modal-card course-modal">
        <button className="close-modal" onClick={onClose}><X /></button>
        <h2>{course.title}</h2>
        <p className="lead">{course.summary}</p>
        <div className="detail-grid">
          <div><strong>What the course covers</strong><p>{course.covers}</p></div>
          <div><strong>Who it is for</strong><p>{course.audience}</p></div>
          <div><strong>Duration</strong><p>{course.duration}</p></div>
          <div><strong>How to enrol</strong><p>{course.enrolment}</p></div>
        </div>
        <strong>What students will learn</strong>
        <ul>{(course.outcomes || []).map((item) => <li key={item}>{item}</li>)}</ul>
        <a className="btn whatsapp" href={waLink(settings, `Hello Parvez Signature, please send me details for ${course.title}.`)} onClick={() => trackLink("whatsapp_click", `${course.title} details`, "Course Modal")} target="_blank" rel="noreferrer">Chat with Us on WhatsApp</a>
      </div>
    </div>
  );
}

function Testimonials({ testimonials }) {
  return (
    <section className="section-pad ivory">
      <SectionTitle eyebrow="Client Stories" title="What Clients and Students Say" text="Prepared for real client testimonials, student success stories, fashion class reviews and before-and-after transformations." />
      <div className="testimonial-grid">
        {testimonials.map((item) => <article className="testimonial" key={item.id}><img src={imageUrl(item.photo || "/media/founder.jpg")} alt={item.client_name} /><p>“{item.text}”</p><h4>{item.client_name}</h4><span>{item.category}</span></article>)}
      </div>
    </section>
  );
}

function InstagramSection({ settings, media }) {
  return (
    <section className="section-pad social-section">
      <SectionTitle eyebrow="Social" title="Follow Parvez Signature" text="Instagram integration is visible now, with structure ready for future auto-sync." />
      <div className="social-grid">
        {media.slice(0, 5).map((item) => item.type === "video" ? <video key={item.id} src={item.src} muted playsInline controls /> : <img key={item.id} src={imageUrl(item.src)} alt={item.alt || item.title} />)}
      </div>
      <a className="btn primary" href={settings.instagram} target="_blank" rel="noreferrer"><Instagram size={16} /> Follow on Instagram</a>
    </section>
  );
}


function FAQSection({ settings }) {
  const faqs = [
    ["Who is Parvez Signature?", "Parvez Signature is a Lagos fashion house and fashion academy founded by Adenike for custom tailoring, fabrics, ready-to-wear and sewing education."],
    ["Does Parvez Signature teach tailoring?", "Yes. The Fashion Academy supports beginners and improving students with sewing fundamentals, streetwear classes and practical fashion training."],
    ["Can I order from outside Lagos or Nigeria?", "The website is structured for Nigeria nationwide delivery and future international shipping. Please confirm the current delivery option on WhatsApp."],
    ["How do I book?", `Click Chat on WhatsApp or send an enquiry. The active WhatsApp number is ${settings.whatsapp}.`],
  ];
  return (
    <section className="section-pad faq-section">
      <SectionTitle eyebrow="Questions Answered" title="Helpful answers for Google, AI search and real visitors" text="Clear answers help clients understand the business quickly and support discovery for Nigerian fashion, diaspora orders and fashion education searches." />
      <div className="faq-grid">
        {faqs.map(([q, a]) => <article key={q}><h3>{q}</h3><p>{a}</p></article>)}
      </div>
      <div className="market-keywords">
        <span>Fashion Designer in Lagos</span><span>Tailor in Lagos</span><span>Bridal Tailor Lagos</span><span>Aso Oke Designer</span><span>Aso Ebi Tailor</span><span>Ankara Dresses</span><span>Sewing Classes Lagos</span><span>Fashion Academy Nigeria</span>
      </div>
    </section>
  );
}

function Shop({ data, settings }) {
  const [filter, setFilter] = useState("All");
  const categories = ["All", ...new Set(data.products.map((p) => p.category))];
  const products = filter === "All" ? data.products : data.products.filter((p) => p.category === filter);
  return (
    <main className="page-wrap">
      <SectionTitle eyebrow="Shop" title="Products, fabrics and custom order inspiration" text="Browse available and enquiry-based items. Every product can be updated by the owner from the content manager." />
      <div className="filters">{categories.map((cat) => <button key={cat} className={filter === cat ? "active" : ""} onClick={() => setFilter(cat)}>{cat}</button>)}</div>
      <div className="product-grid">{products.map((product) => <ProductCard key={product.id} product={product} settings={settings} />)}</div>
      <ConversionBar settings={settings} />
    </main>
  );
}

function Collections({ data, settings }) {
  const sorted = [...data.collections].sort((a, b) => (a.priority || 0) - (b.priority || 0));
  return (
    <main className="page-wrap">
      <SectionTitle eyebrow="Collections" title="Explore the Parvez Signature categories" text="The category order reflects the business priorities: custom orders, Aso Oke, ready-to-wear, fabrics, fashion school, kiddies and accessories." />
      <div className="collection-grid full">{sorted.map((item) => <div className="collection-card static" key={item.id}><img src={imageUrl(item.image)} alt={item.name} /><div><small>{String(item.priority || "").padStart(2, "0")}</small><h3>{item.name}</h3><p>{item.description}</p></div></div>)}</div>
      <CTAButtons settings={settings} />
    </main>
  );
}

function Academy({ data, settings }) {
  return (
    <main className="page-wrap">
      <SectionTitle eyebrow="Fashion Academy" title="Practical sewing education with Adenike" text="Available courses, beginner classes, streetwear programme, sewing fundamentals, advanced fashion and upcoming workshops." />
      <div className="course-grid">{data.courses.map((course) => <CourseCard key={course.id} course={course} settings={settings} />)}</div>
      <ConversionBar settings={settings} />
    </main>
  );
}

function Gallery({ data, settings }) {
  return (
    <main className="page-wrap">
      <SectionTitle eyebrow="Gallery" title="Authentic Parvez Signature work" text="Founder, workshop, garments, fabrics, students, tailoring process and local videos." />
      <div className="gallery-grid">
        {data.media.map((item) => item.type === "video" ? <video key={item.id} src={item.src} controls muted playsInline /> : <img key={item.id} src={imageUrl(item.src)} alt={item.alt || item.title} />)}
      </div>
      <ConversionBar settings={settings} />
    </main>
  );
}

function About({ settings }) {
  return (
    <main className="page-wrap about-page">
      <section className="about-hero">
        <img src="/media/founder.jpg" alt="Adenike founder of Parvez Signature" />
        <div><SectionTitle eyebrow="About Adenike" title="A respected fashion designer and sewing instructor" text="Parvez Signature is built on craftsmanship, family, education, tailoring and service." /><CTAButtons settings={settings} /></div>
      </section>
      <div className="story-grid">
        {[
          ["My Beginning", "Adenike began by learning tailoring, developing her skills and building confidence through patient practice and dedication."],
          ["Building Parvez Signature", "She started small, served clients carefully and grew the brand through consistency, trust and personal attention."],
          ["Teaching Others", "Fashion education became a natural extension of the studio: sharing knowledge, helping students grow and supporting future designers."],
          ["Today", "Parvez Signature now brings together fashion design, custom tailoring, fabrics, materials and education while building a lasting legacy."],
        ].map(([title, text]) => <article key={title}><h3>{title}</h3><p>{text}</p></article>)}
      </div>
      <ConversionBar settings={settings} />
    </main>
  );
}

function Contact({ settings }) {
  const [form, setForm] = useState({ name: "", email: "", phone: "", interest: "General Website Enquiry", message: "" });
  const [sent, setSent] = useState(false);
  async function submit(e) {
    e.preventDefault();
    const response = await axios.post(`${API}/inquiries`, form);
    echo("form_submit", form.interest, "Contact");
    setSent(response.data?.message || "Thank you. Your enquiry has been received by Parvez Signature.");
    setForm({ name: "", email: "", phone: "", interest: "General Website Enquiry", message: "" });
  }
  return (
    <main className="page-wrap contact-page">
      <SectionTitle eyebrow="Contact" title="Start a conversation with Parvez Signature" text={`All enquiries are directed to ${settings.email}.`} />
      <div className="contact-layout">
        <form className="contact-form" onSubmit={submit} data-testid="contact-form">
          <input required placeholder="Your name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
          <input placeholder="Email address" type="email" value={form.email || ""} onChange={(e) => setForm({ ...form, email: e.target.value })} />
          <input placeholder="Phone / WhatsApp" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} />
          <select value={form.interest} onChange={(e) => setForm({ ...form, interest: e.target.value })}><option>General Website Enquiry</option><option>Contact Us</option><option>Book Custom Order</option><option>Fashion Academy Enquiry</option><option>Fabric Purchase</option><option>Accessories</option></select>
          <textarea required placeholder="Tell us what you need" value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} />
          <button className="btn primary" type="submit" data-testid="contact-form-submit-button"><Mail size={16} /> Send Enquiry</button>
          {sent && <p className="success">{sent}</p>}
        </form>
        <aside className="contact-cards">
          <a href={waLink(settings)} target="_blank" rel="noreferrer"><MessageCircle /> Chat on WhatsApp <span>{settings.whatsapp}</span></a>
          <a href={`mailto:${settings.email}`}><Mail /> Email <span>{settings.email}</span></a>
          <a href={`tel:${settings.phone}`}><Phone /> Phone <span>{settings.phone}</span></a>
          <a href={settings.instagram} target="_blank" rel="noreferrer"><Instagram /> Instagram <span>@parvezsignatures</span></a>
        </aside>
      </div>
    </main>
  );
}

function Admin({ data, settings, reload }) {
  const sections = ["products", "collections", "courses", "testimonials", "media"];
  const [adminSession, setAdminSession] = useState(() => JSON.parse(localStorage.getItem("parvez_admin") || "null"));
  const [login, setLogin] = useState({ username: "contact@parvezsignature.com", password: "" });
  const [passwords, setPasswords] = useState({ current_password: "", new_password: "" });
  const [loginError, setLoginError] = useState("");
  const [section, setSection] = useState("products");
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForms.products);
  const [settingsForm, setSettingsForm] = useState(settings);

  useEffect(() => setSettingsForm(settings), [settings]);

  function startCreate(name = section) {
    setEditing(null);
    setForm({ ...emptyForms[name] });
  }

  function startEdit(item) {
    setEditing(item.id);
    setForm({ ...item });
  }

  async function saveItem(e) {
    e.preventDefault();
    const payload = normalizeForm(section, form);
    if (editing) await axios.put(`${API}/${section}/${editing}`, payload, { headers: { "X-Admin-Token": adminSession.token } });
    else await axios.post(`${API}/${section}`, payload, { headers: { "X-Admin-Token": adminSession.token } });
    startCreate();
    await reload();
  }

  async function deleteItem(id) {
    if (!window.confirm("Delete this item?")) return;
    await axios.delete(`${API}/${section}/${id}`, { headers: { "X-Admin-Token": adminSession.token } });
    await reload();
  }

  async function saveSettings(e) {
    e.preventDefault();
    await axios.put(`${API}/settings`, settingsForm, { headers: { "X-Admin-Token": adminSession.token } });
    await reload();
  }

  async function mediaToBase64(file, key) {
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      if (key === "images") setForm((prev) => ({ ...prev, images: [reader.result] }));
      else if (key === "videos") setForm((prev) => ({ ...prev, videos: [reader.result] }));
      else setForm((prev) => ({ ...prev, [key]: reader.result, src: key === "src" ? reader.result : prev.src }));
    };
    reader.readAsDataURL(file);
  }

  async function submitLogin(e) {
    e.preventDefault();
    try {
      const res = await axios.post(`${API}/admin/login`, login);
      localStorage.setItem("parvez_admin", JSON.stringify(res.data));
      setAdminSession(res.data);
      setLoginError("");
    } catch (err) {
      setLoginError("Login failed. Check the temporary development password.");
    }
  }

  async function changePassword(e) {
    e.preventDefault();
    await axios.post(`${API}/admin/change-password`, passwords, { headers: { "X-Admin-Token": adminSession.token } });
    setAdminSession({ ...adminSession, user: { ...adminSession.user, must_change_password: false } });
    localStorage.setItem("parvez_admin", JSON.stringify({ ...adminSession, user: { ...adminSession.user, must_change_password: false } }));
    setPasswords({ current_password: "", new_password: "" });
    alert("Password updated.");
  }

  if (!adminSession?.token) {
    return <main className="page-wrap admin-page"><SectionTitle eyebrow="Secure Owner Access" title="Parvez Signature Content Manager" text="Temporary owner account: contact@parvezsignature.com. The development password must be changed before production." /><form className="admin-form login-form" onSubmit={submitLogin}><label>Email<input value={login.username} onChange={(e) => setLogin({ ...login, username: e.target.value })} /></label><label>Password<input type="password" value={login.password} onChange={(e) => setLogin({ ...login, password: e.target.value })} /></label><button className="btn primary">Login</button>{loginError && <p className="error">{loginError}</p>}</form></main>;
  }

  return (
    <main className="page-wrap admin-page">
      <SectionTitle eyebrow="Owner Content Manager" title="Simple website management" text="Add, edit or delete products, collections, courses, testimonials, gallery images, videos and homepage featured content without developer involvement." />
      <div className="admin-session"><span>Logged in as {adminSession.user?.username} ({adminSession.user?.role})</span><button onClick={() => { localStorage.removeItem("parvez_admin"); setAdminSession(null); }}>Logout</button></div>{adminSession.user?.must_change_password && <form className="admin-form password-form" onSubmit={changePassword}><h3>Change temporary password</h3><label>Current password<input type="password" value={passwords.current_password} onChange={(e) => setPasswords({ ...passwords, current_password: e.target.value })} /></label><label>New password<input type="password" minLength="10" value={passwords.new_password} onChange={(e) => setPasswords({ ...passwords, new_password: e.target.value })} /></label><button className="btn primary">Update Password</button></form>}<div className="admin-tabs">
        {sections.map((name) => <button key={name} className={section === name ? "active" : ""} onClick={() => { setSection(name); startCreate(name); }}>{name}</button>)}
        <button className={section === "settings" ? "active" : ""} onClick={() => setSection("settings")}>settings</button>
      </div>
      {section === "settings" ? (
        <form className="admin-form" onSubmit={saveSettings}>
          {Object.keys(defaultSettings).map((key) => <label key={key}>{key}<input value={settingsForm[key] || ""} onChange={(e) => setSettingsForm({ ...settingsForm, [key]: e.target.value })} /></label>)}
          <button className="btn primary"><Save size={16} /> Save Settings</button>
        </form>
      ) : (
        <div className="admin-layout">
          <form className="admin-form" onSubmit={saveItem}>
            <h3>{editing ? "Edit" : "Add"} {section}</h3>
            <DynamicFields section={section} form={form} setForm={setForm} mediaToBase64={mediaToBase64} />
            <button className="btn primary" type="submit"><Save size={16} /> Save</button>
            <button className="btn outline" type="button" onClick={() => startCreate()}><Plus size={16} /> New</button>
          </form>
          <div className="admin-list">
            {(data[section] || []).map((item) => <article key={item.id}><div><strong>{item.name || item.title || item.client_name}</strong><span>{item.category || item.type || "Content"}</span></div><button onClick={() => startEdit(item)}><Edit3 size={16} /></button><button onClick={() => deleteItem(item.id)}><Trash2 size={16} /></button></article>)}
          </div>
        </div>
      )}
    </main>
  );
}

function normalizeForm(section, form) {
  const copy = { ...form };
  if (section === "courses" && typeof copy.outcomes === "string") copy.outcomes = copy.outcomes.split("\n").map((x) => x.trim()).filter(Boolean);
  return copy;
}

function DynamicFields({ section, form, setForm, mediaToBase64 }) {
  const set = (key, value) => setForm({ ...form, [key]: value });
  if (section === "products") return <><label>Name<input required value={form.name || ""} onChange={(e) => set("name", e.target.value)} /></label><label>Category<input value={form.category || ""} onChange={(e) => set("category", e.target.value)} /></label><label>Description<textarea value={form.description || ""} onChange={(e) => set("description", e.target.value)} /></label><label>Price<input value={form.price || ""} onChange={(e) => set("price", e.target.value)} /></label><label>Photo<input type="file" accept="image/*" onChange={(e) => mediaToBase64(e.target.files[0], "images")} /></label><label>Video<input type="file" accept="video/*" onChange={(e) => mediaToBase64(e.target.files[0], "videos")} /></label><Check form={form} set={set} name="featured" /></>;
  if (section === "collections") return <><label>Name<input required value={form.name || ""} onChange={(e) => set("name", e.target.value)} /></label><label>Description<textarea value={form.description || ""} onChange={(e) => set("description", e.target.value)} /></label><label>Priority<input type="number" value={form.priority || 0} onChange={(e) => set("priority", Number(e.target.value))} /></label><label>Image<input type="file" accept="image/*" onChange={(e) => mediaToBase64(e.target.files[0], "image")} /></label><Check form={form} set={set} name="featured" /></>;
  if (section === "courses") return <><label>Title<input required value={form.title || ""} onChange={(e) => set("title", e.target.value)} /></label><label>Summary<textarea value={form.summary || ""} onChange={(e) => set("summary", e.target.value)} /></label><label>What it covers<textarea value={form.covers || ""} onChange={(e) => set("covers", e.target.value)} /></label><label>Who it is for<textarea value={form.audience || ""} onChange={(e) => set("audience", e.target.value)} /></label><label>Duration<input value={form.duration || ""} onChange={(e) => set("duration", e.target.value)} /></label><label>Learning outcomes, one per line<textarea value={Array.isArray(form.outcomes) ? form.outcomes.join("\n") : form.outcomes || ""} onChange={(e) => set("outcomes", e.target.value)} /></label><label>Image<input type="file" accept="image/*" onChange={(e) => mediaToBase64(e.target.files[0], "image")} /></label><Check form={form} set={set} name="featured" /></>;
  if (section === "testimonials") return <><label>Client name<input required value={form.client_name || ""} onChange={(e) => set("client_name", e.target.value)} /></label><label>Text<textarea required value={form.text || ""} onChange={(e) => set("text", e.target.value)} /></label><label>Category<input value={form.category || ""} onChange={(e) => set("category", e.target.value)} /></label><label>Photo<input type="file" accept="image/*" onChange={(e) => mediaToBase64(e.target.files[0], "photo")} /></label><Check form={form} set={set} name="featured" /></>;
  return <><label>Type<select value={form.type || "image"} onChange={(e) => set("type", e.target.value)}><option>image</option><option>video</option></select></label><label>Title<input required value={form.title || ""} onChange={(e) => set("title", e.target.value)} /></label><label>Alt text<input value={form.alt || ""} onChange={(e) => set("alt", e.target.value)} /></label><label>Category<input value={form.category || ""} onChange={(e) => set("category", e.target.value)} /></label><label>Media file<input type="file" accept="image/*,video/*" onChange={(e) => mediaToBase64(e.target.files[0], "src")} /></label><Check form={form} set={set} name="featured" /></>;
}

function Check({ form, set, name }) {
  return <label className="check"><input type="checkbox" checked={Boolean(form[name])} onChange={(e) => set(name, e.target.checked)} /> Featured on website</label>;
}

function Footer({ settings, setActive }) {
  const showSevenHaven = settings.seven_haven_signature_enabled !== false;
  return (
    <footer className="footer">
      <div className="footer-brand"><span className="footer-logo">PS</span><strong>PARVEZ SIGNATURE</strong><p>Fashion house and fashion academy built on craftsmanship, family, education, tailoring and service. Serving Lagos, Nigerian clients and diaspora enquiries.</p></div>
      <div><h4>Website</h4>{navItems.map((item) => <button key={item} onClick={() => setActive(item)}>{item}</button>)}<button onClick={() => setActive("Admin")}>Owner Content Manager</button></div>
      <div><h4>Social & Contact</h4><a href={waLink(settings)} onClick={() => trackLink("whatsapp_click", "Footer WhatsApp", "Footer")} target="_blank" rel="noreferrer">WhatsApp</a><a href={settings.instagram} onClick={() => trackLink("social_click", "Instagram Footer", "Footer")} target="_blank" rel="noreferrer">Instagram</a><a href={settings.tiktok}>TikTok</a><a href={settings.youtube}>YouTube</a><a href={`mailto:${settings.email}`}>{settings.email}</a><a href={`tel:${settings.phone}`}>{settings.phone}</a><span>{settings.opening_hours}</span></div>
      {showSevenHaven && <a className="seven-haven-mark" href={settings.seven_haven_url || "https://sevenhaventechnologies.com"} target="_blank" rel="noreferrer" aria-label="Seven Haven Technologies"><img src={settings.seven_haven_mark_variant === "black" ? "/media/seven-haven-mark-black.png" : "/media/seven-haven-mark-gold.png"} alt="Seven Haven Technologies quality mark" /></a>}
    </footer>
  );
}

function App() {
  const [active, setActive] = useState("Home");
  const [data, setData] = useState(fallbackData);
  const [loading, setLoading] = useState(true);

  const settings = useMemo(() => ({ ...defaultSettings, ...(data.settings || {}) }), [data.settings]);

  async function loadData() {
    try {
      const response = await axios.get(`${API}/site`);
      setData({ ...fallbackData, ...response.data });
    } catch (error) {
      console.error("Unable to load Parvez Signature content", error);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { loadData(); }, []);

  useEffect(() => {
    document.title = active === "Home" ? "Parvez Signature — Fashion House & Fashion Academy in Lagos" : `${active} — Parvez Signature`;
    echo("page_view", active, active);
  }, [active]);

  const pages = {
    Home: <Home data={data} settings={settings} setActive={setActive} />,
    Shop: <Shop data={data} settings={settings} />,
    Collections: <Collections data={data} settings={settings} />,
    "Fashion Academy": <Academy data={data} settings={settings} />,
    Gallery: <Gallery data={data} settings={settings} />,
    About: <About settings={settings} />,
    Contact: <Contact settings={settings} />,
    Admin: <Admin data={data} settings={settings} reload={loadData} />,
  };

  return (
    <div className="App">
      <Header settings={settings} active={active} setActive={setActive} />
      {loading ? <div className="loading"><span className="monogram">PS</span><p>Preparing Parvez Signature</p></div> : pages[active]}
      <a className="floating-wa" href={waLink(settings)} onClick={() => trackLink("whatsapp_click", "Floating WhatsApp", active)} target="_blank" rel="noreferrer" aria-label="Chat on WhatsApp"><MessageCircle /></a>
      <Footer settings={settings} setActive={setActive} />
    </div>
  );
}

export default App;
